import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const WD = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const pad = (n) => String(n).padStart(2, "0");
const iso = (d, hm) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${hm}`;
export const slotLabel = (s) => { const d = new Date(s); return `${WD[d.getDay()]} ${d.getDate()} · ${pad(d.getHours())}:${pad(d.getMinutes())}`; };

// 3 horários em dias úteis diferentes nos próximos 5 dias.
export function defaultSlots() {
  const out = [], times = ["10:00", "11:30", "15:00"];
  for (let i = 1; i <= 5 && out.length < 3; i += 1) {
    const d = new Date(); d.setDate(d.getDate() + i);
    if (d.getDay() !== 0 && d.getDay() !== 6) out.push(iso(d, times[out.length]));
  }
  return out;
}

const initials = (n) => (n || "?").split(/\s+/).slice(0, 2).map((x) => x[0]).join("").toUpperCase();
const box = "rounded-[14px] border border-slate-100 bg-white p-4";
const inp = "w-full h-11 px-3 rounded-[12px] border border-slate-200 text-[15px]";

function ContactPicker({ k, sugg, pick, setPick }) {
  const [open, setOpen] = useState(!sugg.length);
  const cur = pick.contact_id ? sugg.find((c) => c.id === pick.contact_id) : null;
  return (
    <div className={box} data-testid="prep-contact">
      {cur && <div className="flex items-center gap-3">
        <span className="grid place-items-center w-11 h-11 rounded-full bg-slate-100 font-semibold text-slate-600">{initials(cur.name || cur.entity)}</span>
        <div className="flex-1 min-w-0"><div className="font-semibold text-slate-900 truncate" data-testid="prep-contact-name">{cur.name || cur.entity}</div>
          <div className="text-sm text-slate-500">usado {cur.used_in} {cur.used_in === 1 ? "vez" : "vezes"}</div></div>
        {cur.id === sugg[0]?.id && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold" data-testid="prep-contact-suggested">sugerido</span>}
      </div>}
      {sugg.length > 0 && <button onClick={() => setOpen(!open)} className="mt-2 text-sm text-slate-500 inline-flex items-center gap-1 hover:text-slate-800" data-testid="prep-contact-switch">Trocar <ChevronDown size={14} /></button>}
      {open && <div className="mt-2 space-y-2">
        {sugg.filter((c) => c.id !== pick.contact_id).map((c) => (
          <button key={c.id} onClick={() => { setPick({ contact_id: c.id }); setOpen(false); }} className="w-full text-left px-3 py-2 rounded-[10px] border border-slate-200 hover:bg-slate-50 text-[15px]" data-testid={`prep-contact-opt-${c.id}`}>
            {c.name || c.entity} <span className="text-sm text-slate-500">· usado {c.used_in}</span></button>
        ))}
        <div className="space-y-2 pt-1" data-testid="prep-contact-new">
          <div className="text-sm font-semibold text-slate-700">Novo contacto</div>
          <input className={inp} placeholder="Nome" value={pick.contact_id ? "" : pick.name || ""} onChange={(e) => setPick({ ...pick, contact_id: null, name: e.target.value })} data-testid={`prep-new-name-${k}`} />
          <input className={inp} placeholder="Telemóvel" value={pick.contact_id ? "" : pick.phone || ""} onChange={(e) => setPick({ ...pick, contact_id: null, phone: e.target.value })} data-testid={`prep-new-phone-${k}`} />
          <input className={inp} placeholder="Email (opcional)" value={pick.contact_id ? "" : pick.email || ""} onChange={(e) => setPick({ ...pick, contact_id: null, email: e.target.value })} data-testid={`prep-new-email-${k}`} />
        </div>
      </div>}
    </div>
  );
}

export function PrepSupplierDialog({ pid, k, s, onClose, onDone }) {
  const st = s.kinds[k];
  const sugg = st.suggestions;
  const [pick, setPick] = useState(sugg[0] ? { contact_id: sugg[0].id } : {});
  const [needs, setNeeds] = useState(st.needs_default);
  const [slots, setSlots] = useState(defaultSlots());
  const [busy, setBusy] = useState(false);
  const [prev, setPrev] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      api.post(`/processes/${pid}/prep/${k}/preview`, { contact_id: pick.contact_id || null, name: pick.contact_id ? null : pick.name, needs, slots: slots.filter(Boolean) })
        .then(({ data }) => setPrev(data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [pid, k, pick, needs, slots]);
  const send = async (channel) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/processes/${pid}/prep/${k}/request`, { contact: pick, needs, slots: slots.filter(Boolean), channel });
      if (data.wa_url) window.open(data.wa_url, "_blank");
      toast.success(channel === "email" ? "Pedido enviado por email" : "Mensagem pronta no WhatsApp");
      onDone(); onClose();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-slate-50" data-testid={`prep-supplier-dialog-${k}`}>
        <DialogHeader><DialogTitle className="font-heading text-ec-green">Pedir: {st.label.toLowerCase()}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <ContactPicker k={k} sugg={sugg} pick={pick} setPick={setPick} />
          <div className={box}>
            <div className="font-semibold text-slate-900 mb-2">O que precisa</div>
            <div className="flex flex-wrap gap-2">{st.needs_options.map((n) => { const on = needs.includes(n); return (
              <button key={n} onClick={() => setNeeds(on ? needs.filter((x) => x !== n) : [...needs, n])} className={`h-10 px-3.5 rounded-full border text-[15px] inline-flex items-center gap-1 transition-colors ${on ? "border-emerald-400 bg-emerald-50 text-emerald-700 font-semibold" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid={`prep-need-${n.replace(/\s/g, "-").toLowerCase()}`}>
                {on && <Check size={14} />}{n}</button>); })}</div>
          </div>
          <div className={box}>
            <div className="font-semibold text-slate-900 mb-2">Horários a propor</div>
            <div className="space-y-2">{slots.map((v, i) => (
              <input key={i} type="datetime-local" value={v} onChange={(e) => setSlots(slots.map((x, j) => (j === i ? e.target.value : x)))} className={inp} data-testid={`prep-slot-${i}`} />))}</div>
          </div>
          <div className={box}>
            <div className="font-semibold text-slate-900 mb-2">Mensagem</div>
            <p className="rounded-[12px] bg-lime-50 border border-lime-100 px-3.5 py-3 text-[15px] text-slate-800" data-testid="prep-supplier-message">{prev?.text || "…"}</p>
            <p className="mt-1.5 text-xs text-slate-500">Sem a morada completa nem dados do proprietário.</p>
          </div>
          <button disabled={busy || !needs.length} onClick={() => send("whatsapp")} className="w-full h-12 rounded-[14px] bg-emerald-500 text-white text-[16px] font-semibold hover:bg-emerald-600 disabled:opacity-50 transition-colors" data-testid="prep-supplier-send-wa">Enviar {prev?.to || `ao ${st.who}`} por WhatsApp</button>
          <button disabled={busy || !needs.length} onClick={() => send("email")} className="w-full text-center text-[15px] text-slate-600 underline disabled:opacity-50" data-testid="prep-supplier-send-email">ou por email</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
